"use client";

import { useEffect } from "react";

let holders = 0;
let release: (() => void) | null = null;

function canScrollInside(target: EventTarget | null, deltaX: number, deltaY: number): boolean {
  const portal = target instanceof Element ? target.closest("[data-base-ui-portal]") : null;
  let node = portal ? (target as Element) : null;

  while (node && node !== portal) {
    const style = getComputedStyle(node);

    if (
      deltaY !== 0 &&
      /auto|scroll|overlay/.test(style.overflowY) &&
      (deltaY < 0
        ? node.scrollTop > 0
        : node.scrollTop + node.clientHeight < node.scrollHeight - 1)
    ) {
      return true;
    }

    if (
      deltaX !== 0 &&
      /auto|scroll|overlay/.test(style.overflowX) &&
      (deltaX < 0
        ? node.scrollLeft > 0
        : node.scrollLeft + node.clientWidth < node.scrollWidth - 1)
    ) {
      return true;
    }

    node = node.parentElement;
  }

  return false;
}

function block(): () => void {
  const lockedX = window.scrollX;
  const lockedY = window.scrollY;
  let touchX = 0;
  let touchY = 0;

  const onWheel = (event: WheelEvent) => {

    if (!event.ctrlKey && !canScrollInside(event.target, event.deltaX, event.deltaY)) {
      event.preventDefault();
    }
  };

  const onTouchStart = (event: TouchEvent) => {
    touchX = event.touches[0]?.clientX ?? 0;
    touchY = event.touches[0]?.clientY ?? 0;
  };

  const onTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];

    if (!touch || event.touches.length > 1) {
      return;
    }

    if (!canScrollInside(event.target, touchX - touch.clientX, touchY - touch.clientY)) {
      event.preventDefault();
    }
  };

  const onScroll = () => {
    if (window.scrollX !== lockedX || window.scrollY !== lockedY) {
      window.scrollTo({ left: lockedX, top: lockedY, behavior: "instant" });
    }
  };

  const blocking = { passive: false, capture: true } as const;
  document.addEventListener("wheel", onWheel, blocking);
  document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
  document.addEventListener("touchmove", onTouchMove, blocking);
  window.addEventListener("scroll", onScroll);

  return () => {
    document.removeEventListener("wheel", onWheel, blocking);
    document.removeEventListener("touchstart", onTouchStart, { capture: true });
    document.removeEventListener("touchmove", onTouchMove, blocking);
    window.removeEventListener("scroll", onScroll);
  };
}

export function usePageScrollBlock(): void {
  useEffect(() => {
    holders += 1;
    if (holders === 1) {
      release = block();
    }

    return () => {
      holders -= 1;
      if (holders === 0) {
        release?.();
        release = null;
      }
    };
  }, []);
}

export function PageScrollBlock(): null {
  usePageScrollBlock();
  return null;
}
